import { createApi } from '@reduxjs/toolkit/query/react';

import type { ModelRecordPayment } from '@app/api/Records/types';
import { RECORDS_RESOURCE } from '@app/constants';
import { customBaseQuery } from '@app/services/rtkQuery/baseQuery';
import type {
  CheckRecordTimesRequest,
  CheckRecordTimesResponse,
  ExportRecordsByChainToExcelRequest,
  ExportRecordsByOrganizationToExcelRequest,
  ExportRecordsToExcelRequest,
  ExportRecordsToExcelWithoutGroupingRequest,
  GetClientRecordsRequest,
  GetEmployeesRecordsRequest,
  GetRecordPaymentsRequest,
  PrepareRecordCreationRequest,
  UpdateRecordRequest,
} from '@app/services/rtkQuery/Records/types';
import { RecordsEndpoints as Endpoints } from '@app/services/rtkQuery/Records/types';
import { Methods } from '@app/services/rtkQuery/types';
import type {
  TEmployee,
  NRecords,
  RecordInterface,
  TEmployeeData,
  NFiles,
} from '@app/types';

export const recordsApi = createApi({
  reducerPath: 'recordsApi',
  baseQuery: ({ url, ...props }) =>
    customBaseQuery({ url: `${RECORDS_RESOURCE}/${url}`, ...props }),
  endpoints: ({ query, mutation }) => ({
    [Endpoints.GetEmployeesRecords]: query<
      TEmployee,
      GetEmployeesRecordsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetEmployeesRecords,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetRecordPayments]: mutation<
      ModelRecordPayment[],
      GetRecordPaymentsRequest
    >({
      query: ({ ActiveBranchId, recordIds }) => {
        const queryParams = new URLSearchParams();
        queryParams.set('ActiveBranchId', String(ActiveBranchId));
        recordIds.forEach((id) => queryParams.append('recordIds', String(id)));

        return {
          url: `${Endpoints.GetRecordPayments}?${queryParams.toString()}`,
          method: Methods.GET,
        };
      },
    }),

    [Endpoints.GetClientRecords]: query<
      NRecords.ModelClientRecord[],
      GetClientRecordsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientRecords,
        method: Methods.GET,
        params,
      }),
    }),

    [Endpoints.UpdateRecord]: mutation<RecordInterface, UpdateRecordRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateRecord,
        method: Methods.POST,
        params,
        data,
      }),
    }),

    [Endpoints.PrepareRecordCreation]: query<
      TEmployeeData,
      PrepareRecordCreationRequest
    >({
      query: (params) => ({
        url: Endpoints.PrepareRecordCreation,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CheckRecordTimes]: query<
      CheckRecordTimesResponse,
      CheckRecordTimesRequest
    >({
      query: ({ ...params }) => ({
        url: Endpoints.CheckRecordTimes,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportRecordsByChainToExcel]: mutation<
      NFiles.ModelFile,
      ExportRecordsByChainToExcelRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.ExportRecordsByChainToExcel,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.ExportRecordsByOrganizationToExcel]: mutation<
      NFiles.ModelFile,
      ExportRecordsByOrganizationToExcelRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.ExportRecordsByOrganizationToExcel,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    ExportRecordsToExcelWithoutGrouping: query<
      NFiles.ModelFile,
      ExportRecordsToExcelWithoutGroupingRequest
    >({
      query: ({ ...params }) => ({
        url: 'ExportRecordsToExcelWithoutGrouping',
        method: Methods.GET,
        params,
      }),
    }),
    ExportRecordsToExcel: query<NFiles.ModelFile, ExportRecordsToExcelRequest>({
      query: ({ ...params }) => ({
        url: 'ExportRecordsToExcel',
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useGetEmployeesRecordsQuery,
  useGetRecordPaymentsMutation,
  useGetClientRecordsQuery,
  useLazyGetClientRecordsQuery,
  useUpdateRecordMutation,
  usePrepareRecordCreationQuery,
  useLazyPrepareRecordCreationQuery,
  useCheckRecordTimesQuery,
  useLazyCheckRecordTimesQuery,
  useExportRecordsByChainToExcelMutation,
  useExportRecordsByOrganizationToExcelMutation,
  useLazyGetEmployeesRecordsQuery,
  useLazyExportRecordsToExcelWithoutGroupingQuery,
  useLazyExportRecordsToExcelQuery,
} = recordsApi;
